import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Users, Star, Send, Clock, Check, User, MessageSquare, 
  Loader2, Plus, X, ChevronRight, Award, ThumbsUp, Briefcase
} from 'lucide-react';

const RELATIONSHIP_OPTIONS = [
  { value: 'colleague', label: 'Colleague (same level)' },
  { value: 'manager', label: 'Manager/Supervisor' },
  { value: 'direct_report', label: 'Direct Report' },
  { value: 'cross_functional', label: 'Cross-functional Partner' },
  { value: 'client', label: 'Client/Customer' },
];

const STRENGTH_OPTIONS = [
  'Communication', 'Leadership', 'Problem Solving', 'Teamwork', 'Technical Skills',
  'Creativity', 'Time Management', 'Adaptability', 'Work Ethic', 'Attention to Detail',
  'Strategic Thinking', 'Mentorship', 'Project Management', 'Initiative', 'Reliability'
];

const PeerReviews = () => {
  const [activeTab, setActiveTab] = useState('received');
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [givenReviews, setGivenReviews] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Request modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({ email: '', relationship: 'colleague', message: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  // Submit review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 4,
    strengths: [],
    feedback: '',
    would_work_again: true,
    worked_together_duration: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [received, given, pending, sent] = await Promise.all([
        employeeAPI.getReceivedPeerReviews(),
        employeeAPI.getGivenPeerReviews(),
        employeeAPI.getPendingPeerReviewRequests(),
        employeeAPI.getSentPeerReviewRequests()
      ]);
      setReceivedReviews(received.data || []);
      setGivenReviews(given.data || []);
      setPendingRequests(pending.data || []);
      setSentRequests(sent.data || []);
    } catch (error) {
      console.error('Error fetching peer reviews:', error);
      toast.error('Failed to load peer reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPeerReview = async () => {
    if (!requestData.email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await employeeAPI.requestPeerReview(requestData);
      toast.success('Peer review request sent!');
      setShowRequestModal(false);
      setRequestData({ email: '', relationship: 'colleague', message: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedRequest) return;
    
    setSubmittingReview(true);
    try {
      await employeeAPI.submitPeerReview(selectedRequest.id, reviewData);
      toast.success('Peer review submitted!');
      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewData({
        rating: 4,
        strengths: [],
        feedback: '',
        would_work_again: true,
        worked_together_duration: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleCancelRequest = async (id) => {
    try {
      await employeeAPI.cancelPeerReviewRequest(id);
      toast.success('Request cancelled');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const toggleStrength = (strength) => {
    setReviewData(prev => ({
      ...prev,
      strengths: prev.strengths.includes(strength)
        ? prev.strengths.filter(s => s !== strength)
        : [...prev.strengths, strength]
    }));
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-[#5B6472]'} ${
              interactive ? 'cursor-pointer hover:text-yellow-400' : ''
            }`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="peer-reviews-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1E]">Peer Reviews</h1>
            <p className="text-[#5B6472] mt-1">Get and give professional peer reviews</p>
          </div>
          <Button
            onClick={() => setShowRequestModal(true)}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
            data-testid="request-peer-review-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request Peer Review
          </Button>
        </div>

        {/* Pending Requests Banner */}
        {pendingRequests.length > 0 && (
          <Card className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="pending-requests-banner">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">
                {pendingRequests.length} peer review{pendingRequests.length > 1 ? 's' : ''} waiting for you to complete
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('pending')}
                className="ml-auto text-amber-400 hover:text-amber-300"
              >
                View <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F8FF] border border-[#E8EDF5] p-1">
            <TabsTrigger value="received" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Received ({receivedReviews.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Pending ({pendingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="given" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Given ({givenReviews.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg">
              Requests Sent ({sentRequests.length})
            </TabsTrigger>
          </TabsList>

          {/* Received Reviews */}
          <TabsContent value="received" className="mt-6">
            {receivedReviews.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Award className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <p className="text-[#5B6472]">No peer reviews received yet.</p>
                <p className="text-[#5B6472] text-sm mt-2">Request reviews from your colleagues!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {receivedReviews.map(review => (
                  <Card key={review.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                        {review.reviewer_photo ? (
                          <img src={review.reviewer_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-[#5B6472]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#0A0F1E] font-medium">{review.reviewer_name || 'Anonymous'}</span>
                          <span className="text-[#5B6472] text-sm">• {review.relationship}</span>
                        </div>
                        {review.reviewer_title && (
                          <p className="text-[#5B6472] text-sm mb-2">{review.reviewer_title}</p>
                        )}
                        <div className="flex items-center gap-4 mb-3">
                          {renderStars(review.rating)}
                          {review.would_work_again && (
                            <span className="flex items-center gap-1 text-green-400 text-sm">
                              <ThumbsUp className="w-4 h-4" /> Would work together again
                            </span>
                          )}
                        </div>
                        {review.strengths && review.strengths.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {review.strengths.map(strength => (
                              <span key={strength} className="px-2 py-1 bg-[#1B5AFF]/20 text-[#1B5AFF] rounded text-xs">
                                {strength}
                              </span>
                            ))}
                          </div>
                        )}
                        {review.feedback && (
                          <p className="text-[#5B6472] text-sm italic">"{review.feedback}"</p>
                        )}
                        <p className="text-[#666680] text-xs mt-2">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Reviews to Complete */}
          <TabsContent value="pending" className="mt-6">
            {pendingRequests.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-[#5B6472]">No pending peer reviews to complete.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map(request => (
                  <Card key={request.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                          {request.requestee_photo ? (
                            <img src={request.requestee_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium">{request.requestee_name || 'Someone'}</p>
                          {request.requestee_title && (
                            <p className="text-[#5B6472] text-sm">{request.requestee_title}</p>
                          )}
                          <p className="text-[#666680] text-xs mt-1">
                            Requested {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedRequest(request);
                          setReviewData(prev => ({ ...prev, relationship: request.relationship }));
                          setShowReviewModal(true);
                        }}
                        className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
                        data-testid={`write-review-${request.id}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Write Review
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Given Reviews */}
          <TabsContent value="given" className="mt-6">
            {givenReviews.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <p className="text-[#5B6472]">You haven't given any peer reviews yet.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {givenReviews.map(review => (
                  <Card key={review.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                        {review.reviewee_photo ? (
                          <img src={review.reviewee_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-[#5B6472]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#0A0F1E] font-medium">{review.reviewee_name || 'Anonymous'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {renderStars(review.rating)}
                          <span className="text-[#5B6472] text-sm">• {review.relationship}</span>
                        </div>
                        {review.feedback && (
                          <p className="text-[#5B6472] text-sm mt-2">"{review.feedback}"</p>
                        )}
                        <p className="text-[#666680] text-xs mt-2">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sent Requests */}
          <TabsContent value="sent" className="mt-6">
            {sentRequests.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Send className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <p className="text-[#5B6472]">No peer review requests sent yet.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {sentRequests.map(request => (
                  <Card key={request.id} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[#0A0F1E]">{request.reviewer_email}</p>
                        <p className="text-[#5B6472] text-sm mt-1">
                          {request.status === 'pending' ? (
                            <span className="flex items-center gap-1 text-amber-400">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-green-400">
                              <Check className="w-3 h-3" /> Completed
                            </span>
                          )}
                        </p>
                      </div>
                      {request.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelRequest(request.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Request Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5]">
            <DialogHeader>
              <DialogTitle className="text-[#0A0F1E]">Request Peer Review</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#0A0F1E]">Colleague's Email</Label>
                <Input
                  value={requestData.email}
                  onChange={(e) => setRequestData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="colleague@company.com"
                  className="mt-1 bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
                  data-testid="peer-review-email-input"
                />
              </div>
              <div>
                <Label className="text-[#0A0F1E]">Relationship</Label>
                <select
                  value={requestData.relationship}
                  onChange={(e) => setRequestData(prev => ({ ...prev, relationship: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#F5F8FF] border border-[#E8EDF5] rounded-md text-[#0A0F1E]"
                >
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[#0A0F1E]">Personal Message (optional)</Label>
                <Textarea
                  value={requestData.message}
                  onChange={(e) => setRequestData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Add a personal note..."
                  className="mt-1 bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowRequestModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRequestPeerReview}
                disabled={submittingRequest}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90"
                data-testid="send-peer-review-request-btn"
              >
                {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Submit Review Modal */}
        <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#0A0F1E]">
                Write Peer Review for {selectedRequest?.requestee_name || 'Colleague'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label className="text-[#0A0F1E]">Overall Rating</Label>
                <div className="mt-2">
                  {renderStars(reviewData.rating, true, (rating) => setReviewData(prev => ({ ...prev, rating })))}
                </div>
              </div>
              <div>
                <Label className="text-[#0A0F1E]">Key Strengths (select up to 5)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {STRENGTH_OPTIONS.map(strength => (
                    <button
                      key={strength}
                      onClick={() => toggleStrength(strength)}
                      disabled={!reviewData.strengths.includes(strength) && reviewData.strengths.length >= 5}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        reviewData.strengths.includes(strength)
                          ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                          : 'bg-[#F5F8FF] text-[#5B6472] hover:bg-[#F5F8FF]'
                      } ${!reviewData.strengths.includes(strength) && reviewData.strengths.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {strength}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#0A0F1E]">Feedback</Label>
                <Textarea
                  value={reviewData.feedback}
                  onChange={(e) => setReviewData(prev => ({ ...prev, feedback: e.target.value }))}
                  placeholder="Share your experience working with this person..."
                  className="mt-1 bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
                  rows={4}
                />
              </div>
              <div>
                <Label className="text-[#0A0F1E]">Would you work with this person again?</Label>
                <div className="flex gap-4 mt-2">
                  <button
                    onClick={() => setReviewData(prev => ({ ...prev, would_work_again: true }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      reviewData.would_work_again ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-[#F5F8FF] text-[#5B6472]'
                    }`}
                  >
                    <ThumbsUp className="w-4 h-4" /> Yes
                  </button>
                  <button
                    onClick={() => setReviewData(prev => ({ ...prev, would_work_again: false }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      !reviewData.would_work_again ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#F5F8FF] text-[#5B6472]'
                    }`}
                  >
                    <X className="w-4 h-4" /> No
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowReviewModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90"
                data-testid="submit-peer-review-btn"
              >
                {submittingReview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Submit Review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PeerReviews;
